import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { initInactivityTimer, cleanupInactivityTimer } from '../services/inactivityTimer.service';
import { getCurrentUser } from '../services/auth.service';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Hydrate from localStorage for an instant first paint...
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch {
        setUser(null);
      }
      initInactivityTimer();

      // ...then refresh role/permissions from the server so access changes take effect.
      getCurrentUser()
        .then((res) => {
          if (res?.user) {
            setUser(res.user);
            localStorage.setItem('user', JSON.stringify(res.user));
          }
        })
        .catch(() => {
          /* keep the cached user; apiClient handles hard 401s */
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (userData, token) => {
    setUser(userData);
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    initInactivityTimer();
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    cleanupInactivityTimer();
  };

  const isSuperAdmin = user?.roleType === 'super_admin';
  const isAdmin = user?.roleType === 'admin';
  // Super admins and admins both have unrestricted module access.
  const hasFullAccess = isSuperAdmin || isAdmin;

  const hasModuleAccess = useCallback(
    (moduleKey) => {
      if (!moduleKey) return true; // ungated
      if (hasFullAccess) return true; // super admin / admin have everything
      return Array.isArray(user?.permissions) && user.permissions.includes(moduleKey);
    },
    [hasFullAccess, user],
  );

  const value = useMemo(
    () => ({ user, login, logout, loading, isSuperAdmin, isAdmin, hasFullAccess, hasModuleAccess }),
    [user, loading, isSuperAdmin, isAdmin, hasFullAccess, hasModuleAccess],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
